import * as React from "react";
import { TemplateSub } from "../../../components/layouts/TemplateSub";
import NewButton from "../../../components/uiMerge/newButton";
import { CheckboxField as ToggleField } from "../../shared/ToggleField";
import { Form, Field, Formik } from "formik";
import { Select } from "antd";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ToastMessage from "../../../hooks/useToast";
import { extractError } from "../../../utils/extractError";
import { UpdateAppSettingController, MyAppSettingController } from "@njoy/controller";
import { SelectField } from "src/modules/shared/SelectField";
import { useFindAllLocations } from "@njoy/controller";
import PageLoader from "src/modules/shared/PageLoader";
import {
  useDisableMyPushSubscriptions,
  useRegisterPushSubscription,
  useUnregisterPushSubscription,
} from "@njoy/controller";
import {
  disableBrowserPush,
  enableBrowserPush,
} from "src/utils/pushNotifications";

const { Option } = Select;
const NOTIFICATION_HINT_KEY = "njoy-notification-hint-shown";

const shouldShowNotificationHint = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(NOTIFICATION_HINT_KEY) !== "1";
};

const markNotificationHintShown = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(NOTIFICATION_HINT_KEY, "1");
};

export const AppSettingsPage: React.FC = () => {
  const { registerPushSubscription } = useRegisterPushSubscription();
  const { unregisterPushSubscription } = useUnregisterPushSubscription();
  const { disableMyPushSubscriptions } = useDisableMyPushSubscriptions();

  const { locations, loading: locationLoading } = useFindAllLocations();

  return (
    <MyAppSettingController>
      {({ data, loading }) => {
        if (loading || !data) {
          return <PageLoader />;
        }

        // console.log(match.appSettings)

        const initialValues = {
          isNotification: data.myAppSetting?.isNotification || false,
          isEmail: data.myAppSetting?.isEmail || false,
          isPush: data.myAppSetting?.isPush || false,
          isSMS: data.myAppSetting?.isSMS || false,
          locationId: data.myAppSetting?.location?.id
        };

        const refactoredLocations = locations ? locations
          .slice()
          .sort((a: any, b: any) => a.city.localeCompare(b.city))
          .map((l: any) => ({
            id: l.id,
            label: l.city
          })) : [];

        return(
          <TemplateSub page={`App Settings`}>
            <ToastContainer />

            <div className="px-[1rem] min-h-[98vh]">
              <UpdateAppSettingController>
                {({submitUpdateAppSetting, message}) => (
                  <Formik
                    initialValues={initialValues}
                    onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                      setSubmitting(true);

                      const wantsPushEnabled = values.isNotification && values.isPush;

                      if (wantsPushEnabled) {
                        const result = await enableBrowserPush({
                          registerPushSubscription,
                          unregisterPushSubscription,
                        });

                        if (!result.enabled) {
                          const cancelled = result.reason === "denied" || result.reason === "default";
                          ToastMessage(
                            "warning",
                            cancelled
                              ? "Notification permission was not granted. Changes were not saved."
                              : "Push setup could not be completed. Changes were not saved.",
                          );
                          resetForm();
                          setSubmitting(false);
                          return;
                        }

                        if (shouldShowNotificationHint()) {
                          ToastMessage(
                            "warning",
                            "Please be sure to enable notifications in your device OS and browser settings.",
                          );
                          markNotificationHintShown();
                        }
                      }

                      const errors = await submitUpdateAppSetting({ input: values });
                      if (!errors.errors) {
                        if (!wantsPushEnabled) {
                          await disableBrowserPush({
                            unregisterPushSubscription,
                            disableMyPushSubscriptions,
                          });
                        }

                        ToastMessage("success", message || "App settings updated!" );
                        resetForm({ values });
                      } else {
                        // Only set Formik errors if errors is an object with matching keys
                        if (typeof errors === "object" && errors !== null && !Array.isArray(errors)) {
                          setErrors(errors as any);
                        }
                        ToastMessage("error", extractError(errors.errors));
                      }

                      setSubmitting(false);
                    }}
                  >
                    {({ isSubmitting, errors, touched }) => (
                      <Form>
                        <div className="mt-[1.5rem]">
                          <p className="text-[white] text-[16px] ">
                            App Notifications
                          </p>
 
                          <div className="flex flex-col gap-[10px] mt-[1rem] w-full">
                            <div className="w-full justify-between flex items-center gap-[16px] h-[54px] p-5 bg-[#171820] border border-[#171820] text-white rounded-lg">
                              <p className="text-[white] text-[16px] ">
                                Notification
                              </p>
                              <Field
                                component={ToggleField}
                                name="isNotification"
                                label=""
                              />
                            </div>
                          </div>
                        </div>

                        {/* <div className="flex flex-col gap-[10px] mt-[1rem] w-full">
                          <div className="w-full justify-between flex items-center gap-[16px] h-[54px] p-5 bg-[#171820] border border-[#171820] text-white rounded-lg">
                            <p className="text-[white] text-[16px] ">Notification</p>
                            <div className="flex flex-col w-[180px]">
                              <Field component={ToggleField} name="isNotification" label="" />
                              {touched.isNotification && errors.isNotification && (
                                <span className="text-red-400 text-xs mt-1">{errors.isNotification}</span>
                              )}
                            </div>
                          </div>
                        </div> */}

                        <div className="mt-[1.5rem]">
                          <p className="text-[white] text-[16px] ">Marketing Preferences</p>

                          <div className="flex flex-col gap-[10px] mt-[1rem]">
                            <div className="w-full justify-between flex items-center gap-[16px] h-[54px] p-5 bg-[#171820] border border-[#171820] text-white rounded-lg">
                              <p className="text-[white] text-[16px] ">Push Notification</p>
                              <div className="flex flex-col">
                                <Field component={ToggleField} name="isPush" label="" />
                                {touched.isPush && errors.isPush && (
                                  <span className="text-red-400 text-xs mt-1">{errors.isPush}</span>
                                )}
                              </div>
                            </div>
                            <div className="w-full justify-between flex items-center gap-[16px] h-[54px] p-5 bg-[#171820] border border-[#171820] text-white rounded-lg">
                              <p className="text-[white] text-[16px] ">Email Notification</p>
                              <div className="flex flex-col ">
                                <Field component={ToggleField} name="isEmail" label="" />
                                {touched.isEmail && errors.isEmail && (
                                  <span className="text-red-400 text-xs mt-1">{errors.isEmail}</span>
                                )}
                              </div>
                            </div>
                            <div className="w-full justify-between flex items-center gap-[16px] h-[54px] p-5 bg-[#171820] border border-[#171820] text-white rounded-lg">
                              <p className="text-[white] text-[16px] ">SMS Notification</p>
                              <div className="flex flex-col">
                                <Field component={ToggleField} name="isSMS" label="" />
                                {touched.isSMS && errors.isSMS && (
                                  <span className="text-red-400 text-xs mt-1">{errors.isSMS}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-[1.5rem]">
                          <p className="text-[white] text-[16px]">
                            Change Location
                          </p>

                          <div className="flex flex-col gap-[10px] mt-[1rem]">
                            <Field name="locationId" component={SelectField}>
                              <Option value="" disabled>
                                Select location
                              </Option>
                              {locationLoading ? (
                                <Option value="" disabled>
                                  Loading...
                                </Option>
                              ) : 
                              refactoredLocations ? (
                                refactoredLocations.map((c: any) => (
                                  <Option key={c.id} value={c.id}>
                                    {c.label}
                                  </Option>
                                ))
                              ) : (<></>)}
                            </Field>
                          </div>
                        </div>

                        <div className="mt-[1.5rem]">
                          <NewButton
                            loading={isSubmitting}
                            fullWidth
                            variant="primary"
                            type="submit"
                            className="login-form-button"
                          >
                            Save Changes
                          </NewButton>
                        </div>
                      </Form>
                    )}
                    </Formik>
                  )}
                </UpdateAppSettingController>
            </div>
          </TemplateSub>
        );
      }}
    </MyAppSettingController>
  );
};
